import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Globe, Languages, Check, Loader2, ChevronDown, AlertTriangle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * CulturalPanel Component
 * Multi-language and cultural adaptation settings
 */
export default function CulturalPanel() {
  const [loading, setLoading] = useState(true);
  const [languages, setLanguages] = useState([]);
  const [cultures, setCultures] = useState([]);
  const [selectedCulture, setSelectedCulture] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState('en');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [langRes, cultRes] = await Promise.all([
        axios.get(`${API}/cultural/supported-languages`),
        axios.get(`${API}/cultural/cultures`)
      ]);
      setLanguages(langRes.data.languages || []);
      setCultures(cultRes.data.cultures || []);
      
      // Set default culture
      const defaultCulture = cultRes.data.cultures?.find(c => c.code === 'en-US');
      if (defaultCulture) {
        fetchCultureDetails('en-US');
      }
    } catch (error) {
      console.error('Failed to fetch cultural data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCultureDetails = async (code) => {
    try {
      const response = await axios.get(`${API}/cultural/cultures/${code}`);
      setSelectedCulture(response.data);
    } catch (error) {
      console.error('Failed to fetch culture details:', error);
    }
  };

  if (loading) {
    return (
      <Card className="glass-card border-white/5 animate-pulse">
        <CardContent className="p-6">
          <div className="h-32 bg-white/5 rounded" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass-card border-white/5">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Globe className="w-5 h-5 text-primary" />
          Cultural Adaptation
          <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-xs">
            {cultures.length} Regions
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Language Selector */}
        <div>
          <label className="text-xs text-muted-foreground mb-2 block">
            Content Language
          </label>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-full justify-between" data-testid="language-selector">
                <span className="flex items-center gap-2">
                  {languages.find(l => l.code === selectedLanguage)?.flag}
                  {languages.find(l => l.code === selectedLanguage)?.name || 'Select Language'}
                </span>
                <ChevronDown className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56 max-h-64 overflow-y-auto">
              {languages.map((lang) => (
                <DropdownMenuItem 
                  key={lang.code}
                  onClick={() => setSelectedLanguage(lang.code)}
                  className="flex items-center gap-2"
                >
                  <span>{lang.flag}</span>
                  <span>{lang.name}</span>
                  {selectedLanguage === lang.code && (
                    <Check className="w-4 h-4 ml-auto text-green-500" />
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Culture Selector */}
        <div>
          <label className="text-xs text-muted-foreground mb-2 block">
            Target Culture/Region
          </label>
          <div className="grid grid-cols-3 gap-2">
            {cultures.slice(0, 6).map((culture) => (
              <Button
                key={culture.code}
                variant={selectedCulture?.code === culture.code ? "default" : "outline"}
                size="sm"
                className="text-xs"
                onClick={() => fetchCultureDetails(culture.code)}
                data-testid={`culture-${culture.code}`}
              >
                {culture.region}
              </Button>
            ))}
          </div>
          {cultures.length > 6 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="w-full mt-2 text-xs">
                  +{cultures.length - 6} more regions
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {cultures.slice(6).map((culture) => (
                  <DropdownMenuItem 
                    key={culture.code}
                    onClick={() => fetchCultureDetails(culture.code)}
                  >
                    {culture.region} ({culture.language})
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Selected Culture Info */}
        {selectedCulture && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-3 rounded-lg bg-white/5 border border-white/10 space-y-3"
          >
            <div className="flex items-center justify-between">
              <span className="font-medium text-sm">{selectedCulture.region}</span>
              <Badge variant="outline" className="text-xs">
                {selectedCulture.preferred_tone}
              </Badge>
            </div>
            
            {/* Cultural Notes */}
            {selectedCulture.cultural_notes?.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Cultural Notes:</p>
                <ul className="space-y-1">
                  {selectedCulture.cultural_notes.slice(0, 2).map((note, i) => (
                    <li key={i} className="text-xs flex items-start gap-1.5">
                      <Check className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>{note}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Avoid Topics */}
            {selectedCulture.avoid_topics?.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Topics to Avoid:</p>
                <div className="flex flex-wrap gap-1">
                  {selectedCulture.avoid_topics.map((topic, i) => (
                    <Badge key={i} variant="outline" className="text-xs bg-red-500/10 border-red-500/30 text-red-400">
                      <AlertTriangle className="w-2.5 h-2.5 mr-1" />
                      {topic}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Holidays */}
            {selectedCulture.holidays?.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Key Holidays:</p>
                <div className="flex flex-wrap gap-1">
                  {selectedCulture.holidays.slice(0, 3).map((holiday, i) => (
                    <Badge key={i} variant="outline" className="text-xs">
                      {holiday.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </CardContent>
    </Card>
  );
}
